import { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useButtonPressedCallback, useModalDisplayedEvent } from '@zenbusiness/application-commons-clickstream';
import Button from '@zenbusiness/zui/Button';
import Card from '@zenbusiness/zui/Card';
import Dialog from '@zenbusiness/zui/Dialog';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import * as Styled from '../../AccountSettings.styles';
import { ModalSection } from '../ModalSection';

const ManagePasswordModal = ({
  account, isOpen, onClose
}) => {
  const { resetPassword } = useAuthentication();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [exception, setException] = useState(null);
  const [success, setSuccess] = useState(false);
  const [emailResent, setResent] = useState(false);

  const handleClosePressed = useButtonPressedCallback({
    appSection: 'account',
    elementName: 'manage_password_modal_close',
    elementStyle: 'icon'
  });

  const handleClose = () => {
    onClose();
    handleClosePressed();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await resetPassword(account.accountEmail);
      setSuccess(true);
    } catch (err) {
      setException(err);
    }
  };

  const handleResend = (event) => {
    setResent(true);
    setTimeout(() => {
      setResent(false);
    }, 9000);
    handleSubmit(event);
  };

  if (exception) {
    throw exception;
  }

  useModalDisplayedEvent({
    open: isOpen,
    elementName: 'edit_account_settings'
  });

  if (success) {
    return (
      <Dialog
        data-testid="edit-success"
        fullScreen={fullScreen}
        maxWidth="sm"
        open={isOpen}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Stack sx={{ alignItems: 'center' }}>
            <Styled.CloseButton
              clickstreamProps={{
                appSection: 'account',
                elementName: 'manage_password_modal_close',
                elementStyle: 'icon'
              }}
              data-testid="edit-modal-close"
              icon={<CloseIcon />}
              label="Close Button"
              onClick={onClose}
            />
            <SharedAsset alt="" src="/illustration/subscription-confirmation-email.svg" sx={{ width: { xs: '150px', sm: '235px' } }} />
            <Styled.HeaderModal title={(<Text size="2xl">We&apos;ve sent you a password reset email</Text>)} />
          </Stack>
          <ModalSection
            subtitle={`You should have received an email at ${account.accountEmail} to continue changing your password.`}
          >
            <Button
              clickstreamProps={{
                appSection: 'account',
                elementName: 'manage_password_modal',
                elementStyle: 'button ok'
              }}
              color="primary"
              data-testid="reset-pw"
              onClick={onClose}
              type="submit"
              variant="contained"
            >
              Ok
            </Button>
          </ModalSection>
          {emailResent ? (
            <Text align="center" size="lg" weight="medium" color="primary">Sending email...</Text>
          ) : (
            <Stack flexDirection="row" alignItems="center" justifyContent="center" flexWrap="wrap" gap={1}>
              <Text size="lg" weight="medium" color="primary">
                Didn&apos;t receive an email?
              </Text>
              <Button onClick={handleResend} size="lg" variant="text" color="info">
                Resend email
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      data-testid="edit-account"
      fullScreen={fullScreen}
      maxWidth="sm"
      onClose={handleClose}
      open={isOpen}
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Stack>
          <Styled.CloseButton data-testid="edit-modal-close" onClick={onClose} icon={<CloseIcon />} label="Close Button" />
          <Styled.HeaderModal title={(<Text size="2xl">Update your Info</Text>)} />
        </Stack>
        <ModalSection
          subtitle="We'll email you a link to reset your password."
          title="Need to change your password?"
        >
          <Button
            clickstreamProps={{
              appSection: 'account',
              elementName: 'modal_submit',
              elementStyle: 'primary',
              elementText: 'Reset Your Password'
            }}
            color="primary"
            data-testid="reset-pw"
            onClick={handleSubmit}
            type="submit"
            variant="contained"
          >
            Reset Your Password
          </Button>
        </ModalSection>

        <ModalSection
          subtitle="Contact us through phone, email, or chat."
          title="Need to change your email or phone number?"
        >
          <Link
            data-testid="contact-cs-link"
            clickstreamProps={{
              appSection: 'account',
              destination: 'https://www.zenbusiness.com/contact-us/',
              elementName: 'contact_cs',
              elementText: 'Contact customer service'
            }}
            target="_blank"
            href="/contact-us"
            sx={{ textDecoration: 'none' }}
          >
            <Card
              size="lg"
              sx={{
                display: 'flex',
                justifyContent: { sm: 'center' },
                flexDirection: { sm: 'column' },
                alignItems: 'center',
                gap: 3
              }}
            >
              <SharedAsset alt="" src="/illustration/contact-cs.svg" />
              <Text weight="medium">Contact customer service</Text>
            </Card>
          </Link>
        </ModalSection>
      </DialogContent>
    </Dialog>
  );
};

ManagePasswordModal.propTypes = {
  account: PropTypes.shape({
    accountEmail: PropTypes.string
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ManagePasswordModal;
